import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, Ruler, FileCheck, Users, Sparkles, ShieldCheck, MapPin, Clock, BookOpen, CreditCard, CheckCircle2 } from 'lucide-react';
import { Seo } from '../lib/Seo';
import { pageSeo, pageSchema } from '../seo/staticPages';
import { COMPANY, IMAGES, SOUTH_SOUND_CITIES, NORTH_SOUND_CITIES } from '../lib/siteConfig';
import { TrustBar, CtaBand, Reveal as TeaserReveal, Watermark, CityTicker, HeroPicture, BeforeAfterPair } from '../components/Shared';
import { FamilySection as Section, FamilySectionTitle as SectionTitle, FamilyReveal as Reveal, DisplayHeading, PillButton, PhotoTile, StepRow, CityMarquee, FamilyLenderButtons as LenderButtons, FamilyFinancingLink as FinancingLink } from '../components/brand/Family';
import { HOME_HERO_VIDEO_URL } from '../config/media';
import { ARTICLE_INDEX, articlePath } from '../data/blogIndex'; // light registry — keeps the article bodies out of the homepage bundle
import { ServiceRadiusMap } from '../components/ServiceRadiusMap';
import { trackCall } from '../lib/analytics';
import { srcSetFor, portraitSrcSet, dims, SIZES } from '../lib/img';
import { HOME_BAND_TEXT } from '../config/financing';
import { useHeroImage, useProjects, monthYear } from '../lib/media';

const SEGMENTS = [
  { title: 'Residential Installation', to: '/residential-hvac-installation', slot: 'service_residential', img: IMAGES.residential, alt: 'Residential heat pump indoor and outdoor units', desc: 'Heat pumps, ductless mini-splits, furnaces, and AC — sized with a real Manual J load calculation, never a guess.' },
  { title: 'Commercial Installation', to: '/commercial-hvac-installation', slot: 'service_commercial', img: IMAGES.commercial, alt: 'Commercial rooftop HVAC units on an industrial building', desc: 'RTUs, VRF, make-up air, and tenant improvements — bid from plans, delivered on your schedule.' },
  { title: 'Industrial / Mechanical', to: '/industrial-hvac-installation', slot: 'service_industrial', img: IMAGES.industrial, alt: 'Rows of large-tonnage cooling equipment on an industrial rooftop', desc: 'Large-tonnage systems and process ventilation inside operating facilities, with minimal downtime.' },
  { title: 'Emergency Service', to: '/emergency-hvac-service', slot: 'service_emergency', img: IMAGES.emergency, alt: 'HVAC technician with a tool bag at a home\u2019s front door at night', desc: 'No heat in a cold snap? No cooling in a heat wave? Genuine emergencies jump the schedule — call first, we answer.', urgent: true },
];

const CLEAN_JOBSITE_CHECKLIST = [
  'Floor protection down before the old equipment comes out',
  'Permit pulled before work starts, inspected and closed after',
  'Linesets insulated and covered, condensate and disconnects labeled',
  'Startup readings recorded and handed to you at the walkthrough',
  'Old equipment hauled away, work area swept.',
];

const SegmentCard = ({ s, i }) => {
  const img = useHeroImage(s.slot, s.img, s.alt);
  return (
    <Reveal delay={i * 0.08}>
      <PhotoTile>
        <Link to={s.to} data-testid={`segment-card-${i}`}>
          <img src={img.src} srcSet={srcSetFor(img.src, img.width)} sizes={SIZES.half} {...(img.overridden ? { width: img.width, height: img.height } : dims(img.src))} alt={img.alt} loading="lazy" />
          <div className="idb-photo-tile-content">
            <h3 className="idb-photo-title">{s.title}</h3>
            <p className="idb-photo-description">{s.desc}</p>
            <span className="idb-photo-action">{s.urgent ? 'Get help now' : `Explore ${s.title.split(' ')[0].toLowerCase()}`} <ArrowRight className="idb-photo-arrow" /></span>
          </div>
        </Link>
      </PhotoTile>
    </Reveal>
  );
};


const WHY = [
  { icon: Ruler, title: 'Sized with real math', desc: 'Every system starts with a room-by-room Manual J load calculation — never a square-footage guess.' },
  { icon: FileCheck, title: 'Permit and code fluency', desc: 'Every install is permitted, inspected, and documented to IRC, IMC, and WSEC. No shortcuts.' },
  { icon: Users, title: 'In-house installers', desc: 'The people who quote your job are the people who build it — no subcontractor pool.' },
  { icon: Sparkles, title: 'Clean job sites', desc: 'Floor protection in, daily cleanup, full haul-away. The only evidence we were there is a system that works.' },
  { icon: ShieldCheck, title: 'Workmanship warranty', desc: 'Manufacturer warranties cover the box. Ours covers everything we did to install it.' },
];

const HERO_STATS = [
  { label: 'License', value: `WA #${COMPANY.license}` },
  { label: 'Coverage', value: 'Puget Sound' },
  { label: 'Specialty', value: 'Heat Pumps' },
  { label: 'Response', value: '1 Business Day' },
];

// Hoisted (module scope) so these aren't re-created on every render.
const TICKER_CITIES = [...SOUTH_SOUND_CITIES.slice(0, 10), ...NORTH_SOUND_CITIES.slice(0, 8)];
const GUIDE_IMAGES = [IMAGES.residential, IMAGES.mechanicalRoom, IMAGES.darkIndustrial];

const TeaserOption = ({ value, label }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/estimate-calculator?property_type=${value}`)}
      data-testid={`home-teaser-${value}`}
      className="text-left rounded-[12px] border border-[#D7D2C8] bg-[#FFFCF6] hover:border-[#3E6F8E]/70 hover:bg-white px-4 py-3.5 font-medium text-[15px] text-[#121417] transition-colors duration-200 flex items-center justify-between gap-2 group"
    >
      {label}
      <ArrowRight className="h-4 w-4 text-[#3E6F8E] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </button>
  );
};

export default function HomePage() {
  const hero = useHeroImage('homepage_hero', IMAGES.heroHome, 'Puget Sound home at dusk');
  const heroPortrait = portraitSrcSet(hero.src);
  const { items: projects } = useProjects();
  const featuredPair = (() => {
    const f = projects.find((p) => p.featured);
    if (!f) return null;
    const before = f.photos?.find((ph) => ph.id === f.before_photo_id);
    const after = f.photos?.find((ph) => ph.id === f.after_photo_id);
    return before && after ? { project: f, before, after } : null;
  })();

  return (
    <div className="idb-home">
      <link rel="preload" as="image" href={hero.src} imageSrcSet={heroPortrait || srcSetFor(hero.src, hero.width || 1920)} imageSizes="100vw" media={heroPortrait ? '(max-width: 767px)' : undefined} fetchPriority="high" />
      {heroPortrait && <link rel="preload" as="image" href={hero.src} imageSrcSet={srcSetFor(hero.src, hero.width || 1920)} imageSizes="100vw" media="(min-width: 768px)" fetchPriority="high" />}
      <Seo
        {...pageSeo('/')}
        schema={pageSchema('/')}
      />

      {/* HERO — dark atmospheric canvas + blueprint grid + grain */}
      <div className="relative bg-[#121417] idb-grain overflow-hidden idb-home-hero idb-tone-dark" data-testid="home-hero">
        <HeroPicture src={hero.src} originalWidth={hero.width || 1920} alt={hero.alt} className="absolute inset-0 h-full w-full object-cover idb-home-hero-media" loading="eager" fetchPriority="high" {...(hero.overridden ? { width: hero.width, height: hero.height } : {})} />
        {HOME_HERO_VIDEO_URL && <video className="idb-home-hero-video" src={HOME_HERO_VIDEO_URL} poster={hero.src} autoPlay muted loop playsInline aria-hidden="true" />}
        <div className="idb-home-hero-scrim" />
        <div className="absolute inset-0 idb-blueprint idb-blueprint-dark pointer-events-none" aria-hidden="true" />
        <div className="idb-family-container idb-home-hero-content">
          {/* Keep the original H1; the display tagline remains a paragraph. */}
          <h1 className="idb-eyebrow" data-testid="home-hero-h1">Heat Pump &amp; HVAC Installation in the Puget Sound</h1>
          <DisplayHeading as="p" text="Installed right. Sized right. Guaranteed." split={['Sized right.', 'Guaranteed.']} className="idb-hero-display" data-testid="home-hero-tagline" />
          <p className="idb-home-hero-lede">
            Complete heating and cooling system installations for homes, businesses, and industrial facilities across the Puget Sound — by installers who run the load math, pull the permits, and stand behind the work.
          </p>
          <div className="idb-home-hero-actions">
            <PillButton arrow><Link to="/estimate-calculator" data-testid="hero-pre-estimate-cta">
              Get Your Instant Pre-Estimate
            </Link></PillButton>
            <PillButton variant="secondary-dark"><a href={COMPANY.phoneMainHref} onClick={() => trackCall('hero')} data-testid="hero-call-cta">
              <Phone className="h-4 w-4" /> Call {COMPANY.phoneMain}
            </a></PillButton>
          </div>
          <div className="idb-hero-stat-strip" data-testid="hero-stat-strip">
            <div className="idb-hero-stat-grid">
              {HERO_STATS.map((s) => (
                <div key={s.label} className="idb-hero-stat-item" data-testid={`hero-stat-${s.label.toLowerCase()}`}>
                  <p className="idb-hero-stat-label">{s.label}</p>
                  <p className="idb-hero-stat-value">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 idb-gradient-rule rounded-none z-10" />
      </div>
      <TrustBar withRating />

      {/* 01 — SEGMENTS (bento) */}
      <Section className="overflow-hidden" data-testid="home-segments">
        <Watermark num="01" />
        <SectionTitle title="One crew standard. Every market." sub="From a two-bedroom heat pump conversion in Tacoma to a six-RTU tenant improvement in Kent — plus priority emergency response when a system goes down — the process is the same: engineered sizing, code-fluent installation, clean closeout." />
        <div className="idb-service-grid">
          {SEGMENTS.map((s, i) => <SegmentCard key={s.to} s={s} i={i} />)}
        </div>
      </Section>

      {/* 02 — HOW THE PRE-ESTIMATE WORKS + teaser (dark break) */}
      <Section className="idb-tone-dark idb-grain idb-blueprint idb-blueprint-dark overflow-hidden" data-testid="home-how-it-works">
        <div className="absolute top-0 inset-x-0 idb-gradient-hairline z-10" />
        <Watermark num="02" dark />
        <div className="grid lg:grid-cols-2 gap-12 max-md:gap-8 items-center">
          <div>
            <SectionTitle dark title="Know your number before anyone visits" split="before anyone visits" sub="Most contractors make you book a sales visit to hear a price. Our pre-estimate calculator gives you an honest planning range first — built on the same pricing logic we use in the field." />
            <StepRow as="ol" vertical>
              {[
                ['Tell us about your building', 'Eleven quick questions — size, fuel, ductwork, goals. About two minutes, and you see your number without entering any contact details.'],
                ['Get your instant range', 'A personalized planning range with what’s included and what could move the number. Never a fake single price.'],
                ['We confirm with an on-site measure', 'A free visit with a real load calculation turns your range into a firm, signed quote.'],
              ].map(([t, d], i) => (
                <li key={t}>
                  <span className="idb-step-number">{i + 1}</span>
                  <div>
                    <p className="idb-step-title">{t}</p>
                    <p className="idb-step-body">{d}</p>
                  </div>
                </li>
              ))}
            </StepRow>
          </div>
          <TeaserReveal>
            <div className="relative rounded-[18px] bg-[#FFFCF6] border border-[#D7D2C8] shadow-[var(--idb-shadow-md)] p-6 sm:p-8 idb-brackets lg:-translate-y-4" data-testid="home-calculator-teaser">
              {/* Mirrors the real wizard header at the first question: "Question 1 of 11" and a 0% bar (EstimateWizard stepLabel / progress). */}
              <div className="flex items-center justify-between">
                <span className="idb-spec" data-testid="home-teaser-step-label">Question 1 of 11</span>
                <span className="font-mono-idb text-xs text-[#61676E]">0%</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden" role="presentation"><div className="h-full w-0 rounded-full" style={{ background: 'var(--idb-heatcool)' }} /></div>
              <h3 className="mt-5 font-display text-xl font-semibold text-[#121417]">What type of property is this?</h3>
              <p className="mt-1 text-sm text-[#61676E]">Start here — your answers shape the estimate.</p>
              <div className="mt-4 idb-dimension-rule" />
              <div className="mt-5 grid gap-3">
                <TeaserOption value="single_family" label="Single-family home" />
                <TeaserOption value="townhome_condo" label="Townhome or condo" />
                <TeaserOption value="multi_family" label="Multi-family building" />
                <TeaserOption value="commercial_industrial" label="Commercial or industrial building" />
              </div>
            </div>
          </TeaserReveal>
        </div>
      </Section>

      {/* 03 — WHY IDB */}
      <Section className="overflow-hidden" data-testid="home-why">
        <Watermark num="03" />
        <SectionTitle title="Trades-first. Not a call center." sub="We built this company from the installer’s side of the trade. These are the five things we believe separate a system that performs for fifteen years from one you fight with every winter." />
        <StepRow>
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="idb-step-card">
                <div className="idb-step-top">
                  <span><w.icon /></span>
                  <span className="idb-step-number max-md:hidden">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div>
                  <h3 className="idb-step-title">
                    {w.title}
                    <span className="md:hidden idb-step-number float-right">{String(i + 1).padStart(2, '0')}</span>
                  </h3>
                  <p className="idb-step-body">{w.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4} className="idb-standard-strip">
            <div className="idb-standard-strip-content idb-tone-dark">
              <p className="idb-standard-strip-title">Sized right. Installed clean. Priced straight.</p>
              <PillButton variant="secondary-dark"><Link to="/about" data-testid="home-about-link">Meet the company <ArrowRight className="idb-pill-arrow" /></Link></PillButton>
            </div>
          </Reveal>
        </StepRow>
      </Section>

      {/* 04 — PROOF: a real published project's before/after when one is featured, otherwise the
          honest "how we leave a job site" checklist — never an AI rendering or stock photo. */}
      <Section className="idb-tone-dark overflow-hidden" data-testid="home-projects">
        <Watermark num="04" />
        {featuredPair ? (
          <div className="grid lg:grid-cols-[1fr,400px] gap-10 max-md:gap-6 items-center" data-testid="home-proof-featured">
            <Reveal>
              <BeforeAfterPair before={featuredPair.before} after={featuredPair.after} />
            </Reveal>
            <div>
              <SectionTitle dark title="Proof, side by side" split="side by side" sub={`Same room, same angle \u2014 ${featuredPair.project.city}, ${monthYear(featuredPair.project.completed_date) || 'this year'}.`} />
              <Reveal delay={0.1}>
                <Link to="/projects" data-testid="home-projects-link" className="mt-6 idb-pill idb-pill-secondary-dark">
                  See all projects <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr,420px] gap-10 max-md:gap-6 items-center" data-testid="home-proof-checklist">
            <div>
              <SectionTitle dark title="How we leave a job site" split="a job site" />
              <ul className="mt-6 space-y-3" data-testid="home-proof-checklist-items">
                {CLEAN_JOBSITE_CHECKLIST.map((item) => (
                  <li key={item} className="idb-proof-item">
                    <CheckCircle2 className="h-5 w-5 text-[#2F6B57] shrink-0 mt-0.5" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Reveal delay={0.1}>
                <Link to="/estimate-calculator" data-testid="home-proof-cta" className="mt-6 idb-pill idb-pill-secondary-dark">
                  Get your pre-estimate <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <Reveal>
              <img src={IMAGES.fleet} srcSet={srcSetFor(IMAGES.fleet)} sizes={SIZES.half} {...dims(IMAGES.fleet)} alt="IDB HVAC Solutions service vans" loading="lazy" className="rounded-[16px] border border-[#D7D2C8] w-full h-[280px] sm:h-[320px] object-cover shadow-[var(--idb-shadow-sm)]" data-testid="home-proof-fleet-photo" />
            </Reveal>
          </div>
        )}
      </Section>

      {/* 05 — SERVICE AREA */}
      <Section className="bg-[#EDE9E0] overflow-hidden" data-testid="home-service-area">
        <Watermark num="05" />
        <SectionTitle title="Two offices. One standard. All of Puget Sound." />
        <div className="mt-8 max-md:mt-6">
          <CityTicker cities={TICKER_CITIES} />
        </div>
        <div className="mt-8 max-md:mt-5 grid lg:grid-cols-5 gap-5 max-md:gap-4 items-stretch">
          <div className="lg:col-span-2">
            <Reveal className="h-full">
              <ServiceRadiusMap className="h-full min-h-[420px]" />
            </Reveal>
          </div>
          <div className="lg:col-span-3 grid gap-5 max-md:gap-4 content-start">
            {[
              { name: 'Federal Way HQ — South Sound', to: '/service-areas/federal-way', phoneNode: <a href={COMPANY.phoneMainHref} onClick={() => trackCall('home_area_fw')} className="font-semibold text-[#3E6F8E]">{COMPANY.phoneMain}</a>, addr: `${COMPANY.addressFederalWay}, Federal Way, WA`, radius: 'A 60-mile radius across the South Sound — King and Pierce counties and the I-5 corridor.', img: IMAGES.federalWay, alt: 'Seattle skyline with Mount Rainier, the South Sound region served by our Federal Way HQ' },
              { name: 'Snohomish Branch — North Sound', to: '/service-areas/snohomish', phoneNode: <a href={COMPANY.phoneMainHref} onClick={() => trackCall('home_area_sno')} className="font-semibold text-[#3E6F8E]">{COMPANY.phoneMain}</a>, addr: 'North Sound crews, by appointment', radius: 'A 60-mile radius across the North Sound — Snohomish County and the northern corridor.', img: IMAGES.snohomish, alt: 'Sunset over the Snohomish River estuary and Puget Sound, the North Sound region served by our branch crews' },
            ].map((o, i) => (
              <Reveal key={o.name} delay={i * 0.08}>
                <div className="idb-office-card overflow-hidden h-full sm:flex">
                  {/* Phones: a 112px banner instead of 160px (mobile polish §6); sm and up unchanged. */}
                  <img src={o.img} srcSet={srcSetFor(o.img)} sizes="(max-width: 640px) 100vw, 208px" {...dims(o.img)} alt={o.alt} loading="lazy" className="h-28 sm:h-auto sm:w-52 w-full object-cover shrink-0" />
                  <div className="p-6 max-sm:p-5">
                    <h3 className="idb-office-title"><MapPin className="h-4 w-4 text-[#3E6F8E]" /> {o.name}</h3>
                    <p className="mt-2 text-sm text-[#454B52]">{o.addr} · {o.phoneNode}</p>
                    <div className="mt-3 idb-dimension-rule max-sm:hidden" />
                    <p className="mt-3 max-sm:mt-2 text-sm leading-relaxed text-[#61676E]">{o.radius}</p>
                    <Link to={o.to} className="mt-4 max-sm:mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#3E6F8E] hover:underline decoration-[rgba(208,122,74,0.85)] decoration-2 underline-offset-4 max-md:min-h-11" data-testid={`home-area-link-${i}`}>Office details <ArrowRight className="h-4 w-4" /></Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* 06 — GUIDES (the three field-written articles; real <a href> links, mirrored in the pre-render snapshot) */}
      <Section className="overflow-hidden" data-testid="home-guides">
        <Watermark num="06" />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 max-md:gap-4">
          <SectionTitle title="Guides from the field" split="from the field" sub="The same explanations we give at kitchen tables — heat pump versus furnace, what installations really cost here, and how a gas-to-electric conversion works — written down with real Puget Sound numbers." />
          <Link to="/blog" data-testid="home-guides-all-link" className="idb-pill idb-pill-secondary-light shrink-0 self-start lg:self-auto">
            See all guides <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="idb-guide-grid">
          {ARTICLE_INDEX.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <PhotoTile variant="guide"><Link to={articlePath(a.slug)} data-testid={`home-guide-card-${i}`}>
                <img src={GUIDE_IMAGES[i]} srcSet={srcSetFor(GUIDE_IMAGES[i])} sizes={SIZES.third} {...dims(GUIDE_IMAGES[i])} alt="" aria-hidden="true" loading="lazy" />
                <div className="idb-photo-tile-content">
                  <div className="idb-guide-meta">
                    <span><BookOpen className="h-3.5 w-3.5" /> Guide</span>
                    <span><Clock className="h-3.5 w-3.5" /> {a.readTime}</span>
                  </div>
                  <h3 className="idb-photo-title">{a.title}</h3>
                  <p className="idb-photo-description">{a.excerpt}</p>
                  <span className="idb-photo-action">Read the guide <ArrowRight className="idb-photo-arrow" /></span>
                </div>
              </Link></PhotoTile>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 07 — FINANCING BAND (HFS brief §5, extended Nov 2026 for FTL): slim band above the final call-to-action,
          every lender button from config */}
      <section className="relative bg-[#F4F1EA] border-y border-[#D7D2C8]" data-testid="home-financing-band">
        <div className="idb-family-container idb-tone-dark">
          <div className="idb-financing-layout">
            <div>
              <p className="idb-financing-label"><CreditCard aria-hidden="true" /> Financing</p>
              <p className="mt-2 text-[15px] sm:text-base leading-relaxed text-[#454B52] max-w-2xl" data-testid="home-financing-text">{HOME_BAND_TEXT}</p>
              <FinancingLink className="mt-2" testId="home-financing-how-link" />
            </div>
            <LenderButtons placement="home_band" testId="home-lenders" />
          </div>
        </div>
      </section>

      <CityMarquee cities={TICKER_CITIES} />
      <CtaBand />
    </div>
  );
}
